/**
 * FE-4517 — resolve style classes at render time.
 *
 * A node names its classes in `style.class`, and since FE-4517 those entries are
 * each class's **external_id**, not its CSS name. The bodies arrive with the
 * screen in a `classes` map keyed by that id — exactly as template bodies arrive
 * in `templates` — because the renderer has no classes API and can only render
 * what it is delivered.
 *
 * MIRROR OF THE EDITOR, NOT SHARED CODE. `fractal-fe` resolves the same map in
 * `useClassStyleMap` and merges it in each panel's `mergedStyles`; the two repos
 * ship separately, so this is a deliberate duplicate in the manner of
 * `screenTemplates.ts` and ROOT_FLEX_DEFAULTS. If the precedence changes, BOTH
 * move together or a screen renders differently in the editor than in production.
 *
 * PRECEDENCE: the class is the BASE layer and the node's own value wins. That is
 * what makes a class a default a component may override, and it matches the
 * editor, where `mergedStyles` merges `mergedClasses` first and the node's own
 * style on top.
 */

type AnyRecord = Record<string, any>;

const isPlainObject = (value: any): value is AnyRecord => (
  !!value && typeof value === 'object' && !Array.isArray(value)
);

/** The ids in one `style.class` value. Tolerates the string and array shapes. */
const idsOf = (value: any): string[] => {
  if (typeof value === 'string') return value.split(' ').map((part) => part.trim()).filter(Boolean);
  if (Array.isArray(value)) {
    return value.filter((part) => typeof part === 'string' && !!part.trim()).map((part) => part.trim());
  }
  return [];
};

/**
 * Deep-merge, with `upper` winning.
 *
 * Arrays are REPLACED, never index-merged — `background` is an ordered layer
 * stack and `options` a collection, so element 0 of one bears no relation to
 * element 0 of the other. Same rule the template override merge follows.
 */
const mergeUnder = (base: AnyRecord, upper: AnyRecord): AnyRecord => {
  const out: AnyRecord = { ...base };
  Object.keys(upper).forEach((key) => {
    const value = upper[key];
    const current = out[key];
    out[key] = isPlainObject(value) && isPlainObject(current)
      ? mergeUnder(current, value)
      : value;
  });
  return out;
};

/**
 * Apply the classes named at ONE style scope, and recurse into nested scopes.
 *
 * `class` is legal at every level — `states.hover.class` and
 * `breakpoints.mobile.class` are as valid as the base — because a state override
 * is itself a partial style. Resolving only the base would leave a
 * hover-only class unstyled, which reads as a component that loses its styling
 * the moment you point at it.
 *
 * The `class` key is KEPT after resolution, not stripped: it is still emitted as
 * the DOM `className`, which is what page-level CSS and any author stylesheet
 * hook onto.
 */
const resolveScope = (style: any, classStyles: AnyRecord): any => {
  if (!isPlainObject(style)) return style;

  // Nested scopes first, so a state's own class is resolved before that state is
  // merged over the base below.
  const withScopes: AnyRecord = { ...style };
  Object.keys(withScopes).forEach((key) => {
    if (key === 'class') return;
    if (isPlainObject(withScopes[key])) {
      withScopes[key] = resolveScope(withScopes[key], classStyles);
    }
  });

  const ids = idsOf(withScopes.class);
  if (!ids.length) return withScopes;

  // Left to right, so a later class wins over an earlier one — the cascade order
  // an author writing `"a b"` expects.
  let beneath: AnyRecord = {};
  ids.forEach((id) => {
    const classStyle = classStyles[id];
    if (isPlainObject(classStyle)) beneath = mergeUnder(beneath, classStyle);
  });

  // A class must never carry structure into the node it styles.
  delete beneath.class;
  delete beneath.states;
  delete beneath.breakpoints;

  return mergeUnder(beneath, withScopes);
};

/**
 * The delivered map, flattened to id → style.
 *
 * Two envelopes, because both occur on the wire — the same pair
 * `templateRootOf` handles: the entry is the RECORD (`{ id, name, data }`), or
 * the entry IS the style. Reading only the record form is how the template
 * expansion once silently dropped every reference on a real page.
 */
const classMapCache = new WeakMap<object, AnyRecord>();

export const classStyleMapOf = (classes: any): AnyRecord => {
  if (!isPlainObject(classes)) return {};

  // FE-4577 — memoised on the source map's identity. This used to be handed the
  // handful of classes one screen delivered; it is now handed the branch's
  // ENTIRE accumulated set, on every parse, every cache read and every socket
  // frame. The design-system resolver keeps that map's identity stable while
  // nothing is delivered, so after warm-up this is a single lookup.
  const cached = classMapCache.get(classes);
  if (cached) return cached;

  const flattened = Object.keys(classes).reduce((acc: AnyRecord, key) => {
    const entry = classes[key];
    if (!isPlainObject(entry)) return acc;
    const data = isPlainObject(entry.data) ? entry.data : entry;
    if (isPlainObject(data)) acc[key] = data;
    return acc;
  }, {});

  classMapCache.set(classes, flattened);
  return flattened;
};

/** Does this style scope, or any nested one, actually name a class? */
const scopeNamesClass = (style: any): boolean => {
  if (!isPlainObject(style)) return false;
  if (idsOf(style.class).length > 0) return true;
  return Object.keys(style).some(
    (key) => key !== 'class' && isPlainObject(style[key]) && scopeNamesClass(style[key]),
  );
};

/**
 * FE-4577 — the same pre-scan `expandTemplateRefs` carries, and now needed for
 * the same reason.
 *
 * `applyStyleClasses` used to escape early whenever the DELIVERED class map was
 * empty, which was the common case. Fed the whole branch's set from the store,
 * that map is never empty, so without this a screen naming no class at all would
 * pay a full tree rebuild and lose object identity on every single parse.
 */
const containsClassRef = (nodes: any[]): boolean => nodes.some((node) => {
  if (!isPlainObject(node)) return false;
  if (scopeNamesClass(node.style)) return true;
  return Array.isArray(node.items) && containsClassRef(node.items);
});

/**
 * Resolve every `style.class` in a section list against the delivered classes.
 *
 * Returns the list UNTOUCHED when nothing references a class, so a page that has
 * no classes pays no tree rebuild and keeps object identity on first paint —
 * the same guard `expandTemplateRefs` carries for the same reason.
 *
 * An unresolved id is left in place and simply contributes no style: it may name
 * a class that was deleted or lives on another branch, and dropping the
 * className would also break any CSS hanging off it.
 */
export const applyStyleClasses = (sections: any, classes: any): any => {
  const list = Array.isArray(sections) ? sections : [];
  const classStyles = classStyleMapOf(classes);
  if (!Object.keys(classStyles).length) return sections;
  if (!containsClassRef(list)) return sections;

  const step = (node: any): any => {
    if (!isPlainObject(node)) return node;
    const resolvedStyle = resolveScope(node.style, classStyles);
    const items = Array.isArray(node.items) ? node.items.map(step) : node.items;
    if (resolvedStyle === node.style && items === node.items) return node;
    return { ...node, ...(node.style !== undefined ? { style: resolvedStyle } : {}), ...(node.items !== undefined ? { items } : {}) };
  };

  return list.map(step);
};
